// ============================================================
//  StorePage.js — WhatsApp Store (owner dashboard)
//
//  A ready-made shop on one of the owner's bots: customers type "shop" (or
//  open the store link), browse products with photos, check out in WhatsApp
//  and pay with the owner's own PocketFi. Tabs: Orders · Products · Delivery
//  · Payments · Settings.
// ============================================================
import './StorePage.css';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getStore, getStoreOrders, storeAction } from '../../api/client';
import StoreProducts from './StoreProducts';
import StoreDelivery from './StoreDelivery';
import StorePayments from './StorePayments';
import StoreSettings from './StoreSettings';
import StoreDiscounts from './StoreDiscounts';
import { naira, ago } from './storeUi';

const TABS = [['orders', 'Orders'], ['products', 'Products'], ['discounts', 'Discounts'], ['delivery', 'Delivery'], ['payments', 'Payments'], ['settings', 'Settings']];
const STATUS = {
  pending:    ['Waiting for payment', 'warn'],
  new:        ['New', 'ok'],
  preparing:  ['Preparing', 'warn'],
  on_the_way: ['On the way', 'blue'],
  ready:      ['Ready for pickup', 'blue'],
  delivered:  ['Delivered', 'muted'],
  cancelled:  ['Cancelled', 'muted'],
};
/** The one obvious next step for an order. */
function nextStep(o) {
  if (o.status === 'new') return ['preparing', 'Start preparing'];
  if (o.status === 'preparing') return o.zone === 'Pickup' ? ['ready', 'Ready for pickup'] : ['on_the_way', 'Out for delivery'];
  if (o.status === 'on_the_way' || o.status === 'ready') return ['delivered', 'Mark delivered'];
  return null;
}

export default function StorePage() {
  const [data, setData] = useState(null);
  const [bId, setBId] = useState('');
  const [tab, setTab] = useState('orders');
  const [busy, setBusy] = useState('');
  const [newName, setNewName] = useState('');

  const load = useCallback(async (id = bId) => {
    try {
      const res = await getStore(id || undefined);
      setData(res.data);
      setBId(res.data.botId || '');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not load your store'); setData(d => d || { bots: [] }); }
  }, [bId]);
  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = p => setData(d => ({ ...d, ...p }));

  async function create() {
    setBusy('create');
    try {
      const res = await storeAction('create', bId, { name: newName.trim() });
      toast.success(res.data.message);
      await load(bId);
      setTab('products');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not create the store'); }
    setBusy('');
  }

  if (!data) return <div className="stp"><div className="stp-loading"><span className="stp-spin" /> Loading…</div></div>;

  const botPicker = data.bots?.length > 1 && (
    <label className="stp-botpick">Bot
      <select value={bId} onChange={e => { setData(null); load(e.target.value); }}>
        {data.bots.map(b => <option key={b.id} value={b.id}>{b.name}{b.hasStore ? ' · store' : ''}</option>)}
      </select>
    </label>
  );

  if (!data.bots?.length) {
    return (
      <div className="stp"><div className="stp-card stp-empty">
        <h1>WhatsApp Store</h1>
        <p>Your store runs on one of your WhatsApp bots. Create or connect a bot first, then come back here.</p>
      </div></div>
    );
  }

  if (!data.store) {
    const botName = data.bots.find(b => b.id === bId)?.name || '';
    return (
      <div className="stp">
        <header className="stp-head"><div><h1>WhatsApp Store</h1><p>Sell in WhatsApp: product photos, cart, delivery fees and payment — no website needed.</p></div>{botPicker}</header>
        <section className="stp-card stp-hero">
          <ol className="stp-steps">
            <li><b>1</b><span>Name your store and add products with photos</span></li>
            <li><b>2</b><span>Set delivery areas and fees (or pickup)</span></li>
            <li><b>3</b><span>Connect your PocketFi so customers pay you directly</span></li>
          </ol>
          <label className="stp-field">Store name
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={botName || 'e.g. Tee’s Kitchen'} maxLength={80} />
          </label>
          <button type="button" className="stp-btn stp-btn-brand" onClick={create} disabled={busy === 'create'}>
            {busy === 'create' ? <span className="stp-spin" /> : <i className="fa-solid fa-bag-shopping" />} Create store on {botName || 'this bot'}
          </button>
        </section>
      </div>
    );
  }

  const s = data.store;
  const st = data.stats || {};
  const ready = { products: data.products?.length > 0, payments: data.payments?.connected || s.payOnDelivery, number: !!s.waNumber };

  return (
    <div className="stp">
      <header className="stp-head">
        <div>
          <h1>{s.name}</h1>
          <p>Customers type <b>{s.keywords?.[0] || 'shop'}</b> on WhatsApp, or open your store link.</p>
        </div>
        <div className="stp-head-actions">
          {botPicker}
          <button type="button" className="stp-btn stp-btn-ghost" onClick={() => { navigator.clipboard?.writeText(s.shopUrl); toast.success('Store link copied'); }}>
            <i className="fa-regular fa-copy" /> {s.shopUrl.replace(/^https?:\/\//, '')}
          </button>
          <a className="stp-btn stp-btn-ghost" href={s.shopUrl} target="_blank" rel="noreferrer"><i className="fa-solid fa-arrow-up-right-from-square" /> Open</a>
        </div>
      </header>

      {(!ready.products || !ready.payments || !ready.number) && (
        <div className="stp-todo">
          <b>Finish setting up</b>
          {!ready.products && <button type="button" onClick={() => setTab('products')}><i className="fa-solid fa-plus" /> Add your first product</button>}
          {!ready.payments && <button type="button" onClick={() => setTab('payments')}><i className="fa-solid fa-wallet" /> Connect PocketFi</button>}
          {!ready.number && <button type="button" onClick={() => setTab('settings')}><i className="fa-brands fa-whatsapp" /> Add your WhatsApp number</button>}
        </div>
      )}

      <div className="stp-stats">
        <div className="stp-stat"><span>Orders today</span><b>{st.ordersToday ?? 0}</b><small>{st.waitingPayment ? `${st.waitingPayment} waiting for payment` : 'paid or placed'}</small></div>
        <div className="stp-stat"><span>Sales today</span><b>{naira(st.salesToday)}</b></div>
        <div className="stp-stat"><span>To handle</span><b>{st.toDo ?? 0}</b><small>new, preparing or on the way</small></div>
        <div className="stp-stat is-dark"><span>This month</span><b>{naira(st.salesMonth)}</b></div>
      </div>

      <div className="stp-tabs" role="tablist" aria-label="Store sections">
        {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'is-on' : ''} onClick={() => setTab(k)}>{l}</button>)}
      </div>

      {tab === 'orders' && <Orders bId={bId} initial={data.orders} onStats={stats => patch({ stats })} />}
      {tab === 'products' && <StoreProducts bId={bId} store={s} products={data.products} categories={data.categories || []} onChange={products => patch({ products })} onCategories={categories => patch({ categories })} />}
      {tab === 'discounts' && <StoreDiscounts bId={bId} store={s} discounts={data.discounts || []} onDiscounts={discounts => patch({ discounts })} onStore={store => patch({ store })} />}
      {tab === 'delivery' && <StoreDelivery bId={bId} store={s} zones={data.zones} onSaved={p => patch(p)} />}
      {tab === 'payments' && <StorePayments bId={bId} store={s} payments={data.payments} onStore={store => patch({ store })} onPayments={payments => patch({ payments })} onStats={stats => patch({ stats })} />}
      {tab === 'settings' && <StoreSettings bId={bId} store={s} admins={data.admins || []} onStore={store => patch({ store })} />}
    </div>
  );
}

function Rider({ bId, o, onSaved }) {
  const [name, setName] = useState(o.riderName || '');
  const [phone, setPhone] = useState(o.riderPhone || '');
  const [busy, setBusy] = useState(false);
  const dirty = name !== (o.riderName || '') || phone !== (o.riderPhone || '');
  async function save() {
    setBusy(true);
    try { const r = await storeAction('order_rider', bId, { oId: o.id, name, phone }); toast.success(r.data.message); onSaved(r.data.order); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy(false);
  }
  return (
    <div className="stp-rider" role="group" aria-label={`Rider for order ${o.number}`}>
      <span className="stp-rider-k">🛵 Rider <small>· the customer gets these when it’s out for delivery</small></span>
      <input aria-label="Rider name" value={name} maxLength={60} onChange={e => setName(e.target.value)} placeholder="Name, e.g. Musa" />
      <input aria-label="Rider phone" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0803 123 4567" />
      <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={save} disabled={busy || !dirty}>{busy ? <span className="stp-spin" /> : null}Save rider</button>
    </div>
  );
}

function Orders({ bId, initial, onStats }) {
  const [filter, setFilter] = useState('todo');
  const [orders, setOrders] = useState(initial || []);
  const [busy, setBusy] = useState(0);
  const [open, setOpen] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(0);

  const load = useCallback(async f => {
    try { const res = await getStoreOrders(bId, f); setOrders(res.data.orders); }
    catch (e) { toast.error('Could not load orders'); }
  }, [bId]);
  useEffect(() => { if (filter !== 'todo' || !initial) load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) load(filter); }, 30000); return () => clearInterval(t); }, [filter, load]);

  async function move(o, status) {
    setBusy(o.id);
    try {
      const res = await storeAction('order_status', bId, { oId: o.id, status });
      toast.success(res.data.message);
      onStats(res.data.stats);
      setConfirmCancel(0);
      load(filter);
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not update'); }
    setBusy(0);
  }

  return (
    <section className="stp-card" aria-label="Orders">
      <div className="stp-seg" role="group" aria-label="Filter orders">
        {[['todo', 'To do'], ['unpaid', 'Waiting payment'], ['done', 'Done'], ['all', 'All']].map(([k, l]) =>
          <button key={k} type="button" aria-pressed={filter === k} className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>{l}</button>)}
      </div>
      {orders.length === 0 ? (
        <p className="stp-muted">{filter === 'todo' ? 'Nothing to do right now. New paid orders show up here (and in your email).' : 'No orders here.'}</p>
      ) : (
        <ul className="stp-orders">
          {orders.map(o => {
            const [label, tone] = STATUS[o.status] || [o.status, 'muted'];
            const next = nextStep(o);
            const isOpen = open === o.id;
            return (
              <li key={o.id} className={`stp-order ${isOpen ? 'is-open' : ''}`}>
                <button type="button" className="stp-order-row" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : o.id)}>
                  <span className="stp-order-no">#{o.number}<small>{ago(o.ago)}</small></span>
                  <span className="stp-order-who">{o.name}<small>{o.zone || 'Delivery'}</small></span>
                  <span className="stp-order-items">{o.items.map(i => `${i.qty}× ${i.name}`).join(', ')}</span>
                  <span className="stp-order-total">{naira(o.total)}<small>{o.payMethod === 'cod' ? 'pay on delivery' : o.paid ? 'paid' : 'unpaid'}</small></span>
                  <span className={`stp-pill is-${tone}`}>{label}</span>
                </button>
                {isOpen && (
                  <div className="stp-order-detail">
                    <ul>{o.items.map(i => <li key={i.id}><span>{i.qty}× {i.name}</span><span>{naira(i.qty * i.price)}</span></li>)}
                      {o.discount > 0 && <li className="is-muted"><span>Code {o.discountCode}</span><span>−{naira(o.discount)}</span></li>}
                      <li className="is-muted"><span>{o.zone === 'Pickup' ? 'Pickup' : `Delivery${o.zone ? ' · ' + o.zone : ''}`}</span><span>{o.delivery > 0 ? naira(o.delivery) : 'Free'}</span></li>
                      <li className="is-total"><span>Total</span><span>{naira(o.total)}</span></li>
                    </ul>
                    <div className="stp-order-cust">
                      <span><b>{o.name}</b> · {o.phone}</span>
                      {o.address && <span>To: {o.address}</span>}
                      {o.payRef && <span className="stp-mono">PocketFi {o.payRef}</span>}
                    </div>
                    {o.zone !== 'Pickup' && !['delivered', 'cancelled'].includes(o.status) && (
                      <Rider bId={bId} o={o} onSaved={u => setOrders(list => list.map(x => (x.id === u.id ? u : x)))} />
                    )}
                    {o.riderName && ['delivered', 'cancelled'].includes(o.status) && <p className="stp-muted stp-small">Rider: {o.riderName}{o.riderPhone ? ` · ${o.riderPhone}` : ''}</p>}
                  </div>
                )}
                <div className="stp-order-actions">
                  {next && <button type="button" className="stp-btn stp-btn-dark stp-btn-sm" disabled={busy === o.id} onClick={() => move(o, next[0])}>{busy === o.id ? <span className="stp-spin" /> : null}{next[1]}</button>}
                  {!['delivered', 'cancelled'].includes(o.status) && (
                    <button type="button" className={`stp-btn stp-btn-sm ${confirmCancel === o.id ? 'stp-btn-dangerfill' : 'stp-btn-danger'}`} disabled={busy === o.id}
                      onBlur={() => setConfirmCancel(0)} onClick={() => (confirmCancel === o.id ? move(o, 'cancelled') : setConfirmCancel(o.id))}>
                      {confirmCancel === o.id ? 'Tap again to cancel' : 'Cancel'}
                    </button>
                  )}
                  {o.paid && o.status === 'cancelled' && <span className="stp-note">Paid by PocketFi — refund the customer from your PocketFi dashboard.</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
